/**
 * Módulo Vuex de la bandeja de entrada de WhatsApp.
 *
 * Mantiene el listado de conversaciones, el historial de la conversación
 * abierta y la conexión de tiempo real. El socket es propio y no el global de
 * `vue-native-websocket`: aquel apunta al chat interno del sistema, que es otra
 * cosa y no autentica.
 */
import { isTokenExpired } from '@/store/session'
import whatsappService from '@/services/whatsapp'

export default {
  namespaced: true,

  state: {
    conversations: [],
    current: null,
    messages: [],
    loading: false,
    socket: null,
    connected: false,
    attempts: 0,
    lastEvent: null,
    // Conversación desde la que se fue a cotizar, para poder vincular la
    // cotización cuando se cree.
    conversationToSale: localStorage.getItem('conversationToSale') || null,
    // Derecho al complemento de la empresa activa. Nulo es "todavía no se
    // preguntó", que no es lo mismo que "no lo tiene". Guarda de qué empresa
    // es, así cambiar de empresa lo vuelve a pedir solo.
    entitlement: null
  },

  getters: {
    getConversations: (state) => state.conversations,

    getCurrent: (state) => state.current,

    getMessages: (state) => state.messages,

    getLoading: (state) => state.loading,

    getConnected: (state) => state.connected,

    getLastEvent: (state) => state.lastEvent,

    getConversationToSale: (state) => state.conversationToSale,

    /**
     * Total de conversaciones con mensajes sin leer, para el contador del menú.
     * @param {Object} state - Estado del módulo.
     * @returns {Number} Cantidad de conversaciones pendientes.
     */
    getUnread: (state) =>
      state.conversations.filter((m) => m.unread_count > 0).length,

    getEntitlement: (state) => state.entitlement,

    /**
     * Si la empresa activa tiene contratado el complemento de WhatsApp.
     * @param {Object} state - Estado del módulo.
     * @returns {Boolean} False mientras no se haya preguntado.
     */
    getHasAddon: (state) =>
      state.entitlement ? state.entitlement.enabled : false,

    /**
     * Con quién hablar para contratar el complemento.
     * @param {Object} state - Estado del módulo.
     * @returns {Object} Correo, teléfono y enlace.
     */
    getAddonContact: (state) =>
      state.entitlement && state.entitlement.contact
        ? state.entitlement.contact
        : { email: '', phone: '', url: '' }
  },

  mutations: {
    setConversations(state, conversations) {
      state.conversations = conversations
    },

    setCurrent(state, conversation) {
      state.current = conversation
    },

    setMessages(state, messages) {
      state.messages = messages
    },

    addMessage(state, message) {
      if (state.messages.find((m) => m.pk === message.pk)) {
        return
      }
      state.messages.push(message)
    },

    setLoading(state, loading) {
      state.loading = loading
    },

    /**
     * Sube la conversación al tope y actualiza su vista previa.
     * Evita recargar el listado completo con cada mensaje que entra.
     */
    touchConversation(state, payload) {
      const index = state.conversations.findIndex(
        (m) => m.pk === payload.conversation
      )
      if (index === -1) {
        return
      }
      const conversation = { ...state.conversations[index] }
      if (payload.preview !== undefined) {
        conversation.last_message = payload.preview
      }
      if (payload.unread_count !== undefined) {
        conversation.unread_count = payload.unread_count
      }
      if (payload.state !== undefined) {
        conversation.state = payload.state
      }
      state.conversations.splice(index, 1)
      state.conversations.unshift(conversation)
    },

    setConversationToSale(state, pk) {
      state.conversationToSale = pk
      if (pk) {
        localStorage.setItem('conversationToSale', pk)
      } else {
        localStorage.removeItem('conversationToSale')
      }
    },

    setLastEvent(state, event) {
      state.lastEvent = event
    },

    setSocket(state, socket) {
      state.socket = socket
    },

    setConnected(state, connected) {
      state.connected = connected
      if (connected) {
        state.attempts = 0
      }
    },

    increaseAttempts(state) {
      state.attempts = state.attempts + 1
    },

    /**
     * Cierra el socket y limpia la bandeja al salir de la pantalla.
     * No toca conversationToSale: ese dato tiene que sobrevivir justamente a
     * esta navegación, que es la que lleva al cotizador.
     */
    closeSocket(state) {
      if (state.socket) {
        state.socket.onclose = null
        state.socket.close()
      }
      state.conversations = []
      state.current = null
      state.messages = []
      state.socket = null
      state.connected = false
      state.attempts = 0
      state.lastEvent = null
    },

    resetWhatsapp(state) {
      if (state.socket) {
        state.socket.onclose = null
        state.socket.close()
      }
      state.conversations = []
      state.current = null
      state.messages = []
      state.socket = null
      state.connected = false
      state.attempts = 0
      state.lastEvent = null
      state.conversationToSale = null
      state.entitlement = null
      localStorage.removeItem('conversationToSale')
    },

    setEntitlement(state, entitlement) {
      state.entitlement = entitlement
    }
  },

  actions: {
    setConversations({ commit }, conversations) {
      commit('setConversations', conversations)
    },

    setCurrent({ commit }, conversation) {
      commit('setCurrent', conversation)
    },

    setMessages({ commit }, messages) {
      commit('setMessages', messages)
    },

    addMessage({ commit }, message) {
      commit('addMessage', message)
    },

    setLoading({ commit }, loading) {
      commit('setLoading', loading)
    },

    touchConversation({ commit }, payload) {
      commit('touchConversation', payload)
    },

    /**
     * Pregunta si la empresa tiene contratado el complemento.
     *
     * Si la consulta falla NO se guarda nada: el estado queda nulo y la guarda
     * de ruta lo vuelve a pedir en la navegación siguiente. Guardar un
     * `enabled: false` acá dejaba el complemento apagado por el resto de la
     * sesión a una imprenta que sí lo paga, por un token vencido o un 502 de un
     * reinicio del backend.
     * @param {Number} companyId - Empresa activa.
     */
    async fetchEntitlement({ commit }, companyId) {
      try {
        const response = await whatsappService.entitlement({
          opt: { params: { company_id: companyId }, skipErrorToast: true }
        })
        commit('setEntitlement', { company: companyId, ...response.data })
      } catch (e) {
        commit('setEntitlement', null)
      }
    },

    /**
     * Abre la conexión de tiempo real de la bandeja.
     * @param {Number} companyId - Empresa sobre la que se escucha.
     */
    connect({ state, commit, dispatch }, companyId) {
      if (state.socket || !companyId) {
        return
      }

      // Con el token vencido el servidor rechaza, y reintentar sin mirarlo
      // deja el socket golpeando cada treinta segundos para siempre.
      const token = localStorage.getItem('token')
      if (!token || isTokenExpired(token)) {
        commit('setConnected', false)
        return
      }

      let url = process.env.VUE_APP_WEBSOCKET || ''
      if (process.env.NODE_ENV === 'production') {
        url = `${process.env.VUE_APP_SERVER_WEBSOCKET_TYPE}${location.hostname}${process.env.VUE_APP_SERVER_WEBSOCKET_PORT}/`
      } else {
        url = url.replace(/chat\/$/, '').replace(/'/g, '')
      }

      const socket = new WebSocket(
        `${url}ws/whatsapp/?token=${token}&company_id=${companyId}`
      )

      socket.onopen = () => commit('setConnected', true)

      socket.onmessage = (event) => {
        let data = {}
        try {
          data = JSON.parse(event.data)
        } catch (e) {
          return
        }
        dispatch('handleEvent', data)
      }

      socket.onclose = (event) => {
        commit('setConnected', false)
        commit('setSocket', null)
        if (event.wasClean) {
          return
        }
        commit('increaseAttempts')
        const delay = Math.min(state.attempts * 2000, 30000)
        setTimeout(() => dispatch('connect', companyId), delay)
      }

      commit('setSocket', socket)
    },

    disconnect({ commit }) {
      commit('closeSocket')
    },

    /**
     * Aplica un evento recibido por el socket.
     * @param {Object} payload - Evento difundido por el servidor.
     */
    handleEvent({ state, commit }, payload) {
      commit('touchConversation', payload)
      commit('setLastEvent', payload)

      // El historial sólo se refresca si el evento es de la conversación
      // abierta; el resto se refleja en el listado.
      if (state.current && state.current.pk === payload.conversation) {
        commit('setCurrent', {
          ...state.current,
          state:
            payload.state !== undefined ? payload.state : state.current.state
        })
      }
    }
  }
}
